import { createSlice, createAsyncThunk, PayloadAction, ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { add, getMonth, getYear, format, set } from "date-fns";
import { nonAuthListOfPaymentMethods, reFetchListOfPaymentMethods } from "../../../../shared/hooks/useFetchPaymentMethod";
import API_URLS from "../../Utils/axr";
import { errorConditionGTM } from "../../Utils/googleTags";
import store from "../store";
import { object } from "yup";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import DwLogutil from "../../Utils/DwLogutil";
import { getNonceHeaderValue } from "../../Utils/nonceUtils";
import { updateHeaders } from "./brightspeedToken";
import { toast } from "react-toastify";
import ToastCustonIcon from "../../SharedComponents/toastCustomIcons";

// Utility function to keep only response keys where corresponding param is true
const updateResponseWithTrueKeys = (responseObj: any, payload: any) => {
  if (!responseObj || !payload) return;
  
  const paramToResponseKeyMap: { [key: string]: string } = {
    listofpayment: "paymentMethodListResponse",
    upcoming: "upcomingPaymentDetails",
    paymentHistory: "paymentArrangementHistory",
    fetchPlan: "planDetails"
  };

  Object.keys(paramToResponseKeyMap).forEach((paramKey) => {
    const responseKey = paramToResponseKeyMap[paramKey];
    if (payload[paramKey] !== true && responseObj?.hasOwnProperty?.(responseKey)) {
      delete responseObj[responseKey];
    }
  });
};

type paymentMethodsInitialStateType = {
  paymentMethods: any;
  nonAuthPaymentDetails: any;
  nonAuthPaymentDetailsLoading: boolean;
  loading: boolean;
  error: any;
  nextCreditCardExpirationDate: string;
  nextCreditCardExpirationAllDetail: any;
  addEditPaymentMethodLoading: boolean;
  isListOfPaymentMethodDWLogCalled: boolean;
  paymentMethodAddSection: boolean;
  autoPaymentMethods: any;
  openAutoPayAlertModal: boolean;
  openAutoPayConfirmCVVModal: boolean;
  nextPrimaryPaymentMethod: any;
  selectDeletePrimaryPaymentMethod: any;
  autoPaymentMethodForEdit: any;
  showEditPaymentPopUp: boolean;
  editPrepayCardId: any;
  isPrimaryPaymentMethodChanged: boolean;
  addPaymentMethodError: any;
  primaryPaymentMethod: string | null;
};

const initialState: paymentMethodsInitialStateType = {
  paymentMethods: null,
  nonAuthPaymentDetails: null,
  nonAuthPaymentDetailsLoading: false,
  loading: false,
  error: null,
  nextCreditCardExpirationDate: "",
  nextCreditCardExpirationAllDetail: null,
  addEditPaymentMethodLoading: false,
  isListOfPaymentMethodDWLogCalled: false,
  paymentMethodAddSection: false,
  autoPaymentMethods: null,
  openAutoPayAlertModal: false,
  openAutoPayConfirmCVVModal: false,
  nextPrimaryPaymentMethod: {},
  selectDeletePrimaryPaymentMethod: {},
  autoPaymentMethodForEdit: {},
  showEditPaymentPopUp: false,
  editPrepayCardId:"",
  isPrimaryPaymentMethodChanged: false,
  addPaymentMethodError: null,
  primaryPaymentMethod: null,
};

export const getlistOfPaymentMethods = createAsyncThunk(
  "paymentMethods/list",
  async (billingAccountNumber: string) => {
    try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const isMigrated = reduxStore?.user?.data?.migratedAccount && reduxStore?.user?.data?.migratedAccount?.toUpperCase() === "Y";
      const isPostPaidUser = reduxStore?.brightSpeedUserUtils?.isPostPaidFiberUser;
      const service = {
        default_payment_method : "",
        payment_card_expired : ""
      }
      let url=API_URLS?.listPaymentMethods_QA_Url;
      const response = await reFetchListOfPaymentMethods(
        {
          [url as string]: billingAccountNumber,
        },
        headers
      );

      const nonce = getNonceHeaderValue(response);
      if (nonce) {
        store.dispatch(updateHeaders({
          "nonce": nonce,
        }));
      }

      if(!response?.data?.defaultPaymentMethodId && response?.data?.creditcard?.length > 0) {
        response.data.defaultPaymentMethodId = response?.data?.creditcard[0]?.id;
        service.default_payment_method = "credit card";
        service.payment_card_expired =  response?.data?.creditcard[0]?.isCardExpired;
      }
     
      if (response?.data?.creditcard) {
        const sortedCreditCardResponse = response?.data?.creditcard?.sort(
          (a: any, b: any) =>
            a.id === response?.data?.defaultPaymentMethodId ? -1 : 1
        );
        response.data.creditcard = sortedCreditCardResponse;
        service.default_payment_method = "credit card";
        service.payment_card_expired =  response?.data?.creditcard[0]?.isCardExpired;
      }

      if (
        (!response?.data?.creditcard ||
          response?.data?.creditcard?.length === 0) &&  !isPostPaidUser &&
        (!response?.data?.paypal || response?.data?.paypal?.length === 0)
      ) {
        response.data.disableChangePlanFeature = true;
      }
      if (
        response?.data?.creditcard?.length > 0 &&
        reduxStore?.user?.data?.billingType?.toUpperCase() !== "POSTPAID" &&
        headers?.siteId?.toUpperCase() === "CTL"
      ) {
        const isContainPaypalCard = response?.data?.creditcard?.find(
          (item: any) => item?.type?.toUpperCase() === "PAYPAL"
        );
        if (isContainPaypalCard?.id) {
          response.data.isContainPaypalCard = true;
        }

        if(response?.data?.defaultPaymentMethodId && isContainPaypalCard?.id === response?.data?.defaultPaymentMethodId ){
         service.default_payment_method = "PAYPAL";
         service.payment_card_expired =  response?.data?.creditcard[0]?.isCardExpired;
        }
      }

      const deditCardOrACHEnrolledForDefaultPayment = response?.data?.creditcard?.find((item: any) => (item?.id === response?.data?.defaultPaymentMethodId
        && (item?.type?.trim()?.toUpperCase() === "ATM" || item?.paymentType?.trim()?.toUpperCase() === "ACH"))
      ); 
      
      const creditAsDefaultPayment = response?.data?.creditcard?.find((item: any) => (
        item?.id === response?.data?.defaultPaymentMethodId && 
        item?.type?.trim()?.toUpperCase() === "CreditCard"
      ));
      
      if (deditCardOrACHEnrolledForDefaultPayment) {
        response.data.isACHOrDebitEnrolled = true;
      }

      if((headers?.siteId?.toUpperCase() === "BS" || headers?.siteId?.toUpperCase() === "QF") && reduxStore?.user?.data?.billingType?.toUpperCase() === "POSTPAID" && !isMigrated) {
          if(deditCardOrACHEnrolledForDefaultPayment)  DwLogutil({stepName: EC_DW_LOGGING_STEPS?.ppf_payment_achOrDebit});
          if(creditAsDefaultPayment)  DwLogutil({stepName: EC_DW_LOGGING_STEPS?.ppf_payment_credit});
      }

      DwLogutil({ stepName: EC_DW_LOGGING_STEPS.Payment_services, error: JSON.stringify(service) });
      return response?.data;
    } catch (error) {
      throw error;
    }
  }
);

function dispatch(setHeaders: ActionCreatorWithPayload<any, string>) {
  throw new Error("Function not implemented.");
}
export const getNonAuthPaymentDetails = createAsyncThunk(
  "NonAuthPaymentMethods/list",
  async (
    params: {
      // billingAccountNumber: string;
      listofpayment?: boolean;
      upcoming?: boolean;
      paymentHistory?: boolean;
      fetchPlan?: boolean;
      isPa?: boolean;
    }
  ) => {
     const {  listofpayment, upcoming, paymentHistory, fetchPlan,isPa=false } = params;
         try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const sessionId = sessionStorage?.getItem("sessionId") || reduxStore?.user?.sessionId || "";
      const isMigrated = reduxStore?.user?.data?.migratedAccount && reduxStore?.user?.data?.migratedAccount?.toUpperCase() === "Y";
      const service = {
        default_payment_method : "",
        payment_card_expired : false
      }
      let url=API_URLS?.non_auth_QBP_payment_details;
      const param={
        "": url,
      }
      const body={
        baseline: {
          transactionId: sessionId,
        },
        // "ban": billingAccountNumber,
        "listOfPayments": listofpayment ? true : false,
        "upcomingPayments": upcoming ? true : false,
        "history": paymentHistory ? true : false, 
        "fetchPlans": fetchPlan ? true : false,
      }
 
      const response = await nonAuthListOfPaymentMethods(
        param,
        body,
        {
          ...headers,
          nonce: headers?.nonce || "",
          flowType: isPa ? "pa":"qbp"
        }
      );

      let newAccessToken = "";
      let nonce = "";
      // newAccessToken = response.headers["access-token"] || "";
      nonce = getNonceHeaderValue(response);
      if (nonce) {
        store.dispatch(updateHeaders({
          // ["access-token"]: newAccessToken,
          "nonce": nonce
        }));
      }
      if(!response?.data?.paymentMethodListResponse?.defaultPaymentMethodId && response?.data?.paymentMethodListResponse?.creditcard?.length > 0) {
        response.data.paymentMethodListResponse.defaultPaymentMethodId = response?.data?.paymentMethodListResponse?.creditcard[0]?.id;
        service.default_payment_method = "credit card";
        service.payment_card_expired =  response?.data?.paymentMethodListResponse?.creditcard[0]?.isCardExpired;
      }
    
      if (response?.data?.paymentMethodListResponse?.creditcard) {
        const sortedCreditCardResponse = response?.data?.paymentMethodListResponse?.creditcard?.sort(
          (a: any, b: any) =>
            a.id === response?.data?.paymentMethodListResponse?.defaultPaymentMethodId ? -1 : 1
        );
        response.data.paymentMethodListResponse.creditcard = sortedCreditCardResponse;
        service.default_payment_method = "credit card";
        service.payment_card_expired =  response?.data?.paymentMethodListResponse?.creditcard[0]?.isCardExpired;
      }

      if (
        (!response?.data?.paymentMethodListResponse?.creditcard ||
          response?.data?.paymentMethodListResponse?.creditcard?.length === 0) &&
        (!response?.data?.paymentMethodListResponse?.paypal || response?.data?.paymentMethodListResponse?.paypal?.length === 0)
      ) {
        // response.disableChangePlanFeature = true;
      }
      if (
        response?.data?.paymentMethodListResponse?.creditcard?.length > 0 &&
        // reduxStore?.user?.data?.billingType?.toUpperCase() !== "POSTPAID" &&
        headers?.siteId?.toUpperCase() === "CTL" // need to check on this
      ) {
        const isContainPaypalCard = response?.data?.paymentMethodListResponse?.creditcard?.find(
          (item: any) => item?.type?.toUpperCase() === "PAYPAL"
        );
        if (isContainPaypalCard?.id) {
          response.data.paymentMethodListResponse.isContainPaypalCard = true;
        }

        if(response?.data?.paymentMethodListResponse?.defaultPaymentMethodId && isContainPaypalCard?.id === response?.data?.paymentMethodListResponse?.defaultPaymentMethodId ){
          service.default_payment_method = "PAYPAL";
          service.payment_card_expired =  response?.data?.paymentMethodListResponse?.creditcard[0]?.isCardExpired;
        }
      }

      const deditCardOrACHEnrolledForDefaultPayment = response?.data?.paymentMethodListResponse?.creditcard?.find((item: any) => (item?.id === response?.data?.paymentMethodListResponse?.defaultPaymentMethodId
        && (item?.type?.trim()?.toUpperCase() === "ATM" || item?.paymentType?.trim()?.toUpperCase() === "ACH"))
      ); 
      
      const creditAsDefaultPayment = response?.data?.paymentMethodListResponse?.creditcard?.find((item: any) => (
        item?.id === response?.data?.paymentMethodListResponse?.defaultPaymentMethodId && 
        item?.type?.trim()?.toUpperCase() === "CreditCard"
      ));
      
      if (deditCardOrACHEnrolledForDefaultPayment) {
        // response.paymentMethodListResponse.isACHOrDebitEnrolled = true;
      }

      if((headers?.siteId?.toUpperCase() === "BS" || headers?.siteId?.toUpperCase() === "QF") && reduxStore?.user?.data?.billingType?.toUpperCase() === "POSTPAID" && !isMigrated) {
          if(deditCardOrACHEnrolledForDefaultPayment)  DwLogutil({stepName: EC_DW_LOGGING_STEPS?.ppf_payment_achOrDebit});
          if(creditAsDefaultPayment)  DwLogutil({stepName: EC_DW_LOGGING_STEPS?.ppf_payment_credit});
      }
      
      // Remove response keys where corresponding param is false
      if (response?.data && params) {
        updateResponseWithTrueKeys(response.data, params);
      }

      DwLogutil({ stepName: isPa ? EC_DW_LOGGING_STEPS.unPa_B2cFetchPaymentSuccess : EC_DW_LOGGING_STEPS.unQbp_apiFetchPaymentSuccess });

      if (response?.data?.accountType?.toLowerCase() === 'enterprise') {
         DwLogutil({ stepName: EC_DW_LOGGING_STEPS.unQbp_enterpriseUser });
      }
      if (response?.data?.accountStatus?.toLowerCase() === 'suspended') {
         DwLogutil({ stepName: EC_DW_LOGGING_STEPS.unQbp_suspendedUserAttempted });
      }

      return response;
    } 
    catch (error: any) {
      console.error (error);
      DwLogutil({ stepName: isPa ? EC_DW_LOGGING_STEPS.unPa_apiFetchPaymentFailed : EC_DW_LOGGING_STEPS.unQbp_apiFetchPaymentFailed, error: error?.response?.data?.message || error });
      
      // Time out block
      if (error?.response?.data?.businessError?.code === 'PY_QBP001'){
        DwLogutil({ stepName: EC_DW_LOGGING_STEPS.unQbp_timeOut, error: error?.response?.data?.message || error });
        if(isPa) {
          window.location.href = `/agent-payment-arrangements/time-out`;
        }else {
          window.location.href = `/non-auth-quick-bill-pay/time-out`;
        }
        return;
      }

      // Attempt exceeded
      if (error?.response?.data?.businessError?.code === 'PY_QBP002'){
        DwLogutil({ stepName: !isPa ? EC_DW_LOGGING_STEPS.unQbp_maxAttempted : EC_DW_LOGGING_STEPS.unPa_maxAttempted, error: error?.response?.data?.message || error });
        window.location.href = !isPa ? `/non-auth-quick-bill-pay/attempt-exceeded` : `/non-auth-payment-arrangement/attempt-exceeded` ; 
        return;
      }

      // 502 / any other unsuccessful response -> send the user back to the landing page
      sessionStorage.setItem("nonAuthFetchPaymentFailed", "true");
      window.location.replace(!isPa ? `/non-auth-quick-bill-pay` : `/non-auth-payment-arrangement`);
      
      
    }
  }
);

// Normalizes 2-digit CTL years (e.g. "26") and 4-digit years into a single comparable yyyymm-style index
const toComparableYearMonth = (year: any, month: any) => {
  const yearNumber = Number(year || 0);
  const normalizedYear = yearNumber < 100 ? 2000 + yearNumber : yearNumber;
  return normalizedYear * 12 + (Number(month || 0) - 1);
};

const checkAndGetCreditCardExiration = (listOfPaymentMethodRes: any) => {
  const today = new Date();
  const nextMonthDate = add(today, { months: 3 });
  const windowStart = toComparableYearMonth(getYear(today), getMonth(today) + 1);
  const windowEnd = toComparableYearMonth(getYear(nextMonthDate), getMonth(nextMonthDate) + 1);

  const nextMonthCardWillExp = listOfPaymentMethodRes?.creditcard
    ?.filter((card: any) => card?.expirationMonth && card?.expirationYear)
    ?.sort((a: any, b: any) =>
      toComparableYearMonth(a?.expirationYear, a?.expirationMonth) -
      toComparableYearMonth(b?.expirationYear, b?.expirationMonth)
    )
    ?.find((card: any) => {
      const cardYearMonth = toComparableYearMonth(card?.expirationYear, card?.expirationMonth);
      return cardYearMonth >= windowStart && cardYearMonth <= windowEnd;
    });

  if (nextMonthCardWillExp?.expirationMonth && nextMonthCardWillExp?.expirationYear) {
    const yearRaw = Number(nextMonthCardWillExp.expirationYear || 0);
    const yearNormalized = yearRaw < 100 ? 2000 + yearRaw : yearRaw;
    const expirationDate = set(new Date(), {
      year: yearNormalized,
      month: Number(nextMonthCardWillExp.expirationMonth || 0) - 1, // JS months are 0-indexed
      date: 1
    });

    return {
      creditCardExpirationDate: format(expirationDate, "MMMM yyyy"),
      nextMonthCreditCardExpDetails: nextMonthCardWillExp,
    };
  }
};

const paymentMethodsSlice = createSlice({
  name: "paymentMethods",
  initialState,
  reducers: {
    hideCreditCardExpNotification: (state) => {
      state.nextCreditCardExpirationDate = "";
      state.nextCreditCardExpirationAllDetail = null;
    },
    changePrimaryPaymentMethod: (state, action: PayloadAction<string>) => {
      state.paymentMethods = {
        ...state.paymentMethods,
        defaultPaymentMethodId: action.payload,
      };
    },
    onAddEditPaymentMethodLoadingStart: (state) => {
      state.addEditPaymentMethodLoading = true;
    },
    onAddEditPaymentMethodLoadingStop: (state) => {
      state.addEditPaymentMethodLoading = false;
    },
    setIsListOfPaymentMethodDWLogCalled: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isListOfPaymentMethodDWLogCalled = action.payload;
    },
    clearPaymentMethodsSlice: (state) => {
      return initialState;
    },
    setPaymentMethodAddSection: (state, action: PayloadAction<boolean>) => {
      state.paymentMethodAddSection = action.payload;
    },

    addAutoPaymentMethod: (state, action: PayloadAction<string>) => {
      let newState = { ...state.autoPaymentMethods };
      if (
        newState &&
        newState.creditcard &&
        action?.payload.hasOwnProperty("cardNumber")
      ) {
        newState.creditcard = [...newState.creditcard, action.payload];
      } else if (
        newState &&
        newState.bankAccount &&
        action?.payload.hasOwnProperty("bankAccountNumber")
      ) {
        newState.bankAccount = [...newState.bankAccount, action.payload];
      } else if (action?.payload.hasOwnProperty("bankAccountNumber")) {
        newState = {
          ...state.autoPaymentMethods,
          bankAccount: [action.payload],
        };
      } else {
        newState = {
          ...state.autoPaymentMethods,
          creditcard: [action.payload],
        };
      }
      state.autoPaymentMethods = newState;
    },
    editAutoPaymentMethod: (state, action: PayloadAction<any>) => {
      let newState = { ...state.autoPaymentMethods };
      if (
        newState &&
        newState.creditcard &&
        action?.payload.paymentType == "card"
      ) {
        const index = newState.creditcard.findIndex(
          (card: any) => action?.payload?.cardDetails?.id == card?.id
        );

        newState.creditcard[index].streetaddress =
          action?.payload?.cardDetails?.streetaddress;
        newState.creditcard[index].city = action?.payload?.cardDetails?.city;
        newState.creditcard[index].state = action?.payload?.cardDetails?.state;
        newState.creditcard[index].zipCode =
          action?.payload?.cardDetails?.zipCode;
        newState.creditcard[index].expirationMonth =
          action?.payload?.cardDetails?.expirationMonth;
        newState.creditcard[index].expirationYear =
          action?.payload?.cardDetails?.expirationYear;
        newState.creditcard[index].securityCode =
          action?.payload?.cardDetails?.securityCode;
      } else if (
        newState &&
        newState.bankAccount &&
        action?.payload.paymentType == "bankAccount"
      ) {
        const index = newState.bankAccount.findIndex(
          (bankDetails: any) =>
            action?.payload?.bankDetails?.id == bankDetails?.id
        );

        newState.bankAccount[index].bankAccountType =
          action?.payload?.bankDetails?.bankAccountType;
        newState.bankAccount[index].bankAccountNumber =
          action?.payload?.bankDetails?.bankAccountNumber;
        newState.bankAccount[index].routingNumber =
          action?.payload?.bankDetails?.routingNumber;
        newState.bankAccount[index].accountUserName =
          action?.payload?.bankDetails?.accountUserName;
      }
      state.autoPaymentMethods = newState;
    },
    deleteAutoPaymentMethod: (state, action: PayloadAction<any>) => {
      let newState = { ...state.autoPaymentMethods };
      if (action.payload) {
        newState = {
          ...newState,
          creditcard: state.autoPaymentMethods.creditcard.filter(
            (data: any, index: string) => data.id !== action.payload
          ),
        };
      }
      state.autoPaymentMethods = newState;
    },
    deleteBankPaymentMethod: (state, action: PayloadAction<any>) => {
      let newState = { ...state.autoPaymentMethods };
      if (action.payload) {
        newState = {
          ...newState,
          bankAccount: state.autoPaymentMethods.bankAccount.filter(
            (data: any, index: string) => data.id !== action.payload
          ),
        };
      }
      state.autoPaymentMethods = newState;
    },
    setOpenAutoPayAlertModal: (state, action: PayloadAction<boolean>) => {
      state.openAutoPayAlertModal = action.payload;
    },
    setDefaultAutoPaymentMethodId: (state, action: PayloadAction<string>) => {
      let newState = { ...state.autoPaymentMethods };
      newState = {
        ...state.autoPaymentMethods,
        defaultPaymentMethodId: action.payload,
      };
      state.autoPaymentMethods = newState;
    },
    deleteAllAutoPaymentMethods: (state) => {
      let newState = { ...state.autoPaymentMethods };

      state.autoPaymentMethods = { ...newState, creditcard: [] };
    },
    selectAutoPaymentMethodForEdit: (state, action: PayloadAction<object>) => {
      state.autoPaymentMethodForEdit = action.payload;
    },
    setOpenConfirmCVVModal: (state, action: PayloadAction<boolean>) => {
      state.openAutoPayConfirmCVVModal = action.payload;
    },
    setPrimaryPaymentMethodChanged: (state, action: PayloadAction<boolean>) => {     
      state.isPrimaryPaymentMethodChanged = action.payload;
    },
    setaddPaymentMethodError: (state, action: PayloadAction<any>) => {
      state.addPaymentMethodError = action.payload;
    },
    setNextPrimaryPaymentMethod: (state, action: PayloadAction<object>) => {
      // let newState = { ...state.autoPaymentMethods };
      // newState = { ...state.autoPaymentMethods, nextPrimaryPaymentMethod: action.payload };
      //state.autoPaymentMethods=newState;
      state.nextPrimaryPaymentMethod = action.payload;
    },
    seleteDeletePrimaryPaymentMethodFlag: (
      state,
      action: PayloadAction<boolean>
    ) => {
      let newState = { ...state.autoPaymentMethods };
      newState = {
        ...state.autoPaymentMethods,
        deletePrimaryPaymentMethodFlag: action.payload,
      };
      state.autoPaymentMethods = newState;
    },
    setSelectDeletePrimaryPaymentMethod: (
      state,
      action: PayloadAction<object>
    ) => {
      // let newState = { ...state.autoPaymentMethods };
      // newState = { ...state.autoPaymentMethods, nextPrimaryPaymentMethod: action.payload };
      //state.autoPaymentMethods=newState;
      state.selectDeletePrimaryPaymentMethod = action.payload;
    },
    setAutoPaySettingsUpdated: (state, action: PayloadAction<boolean>) => {
      let newState = { ...state.autoPaymentMethods };
      newState = {
        ...state.autoPaymentMethods,
        autoPaySettingsUpdated: action.payload,
      };
      state.autoPaymentMethods = newState;
    },
    setShowEditPaymentPopUp: (state, action:PayloadAction<boolean>)=>{
      state.showEditPaymentPopUp = action.payload;
    },
    setPrepayCardId: (state, action:PayloadAction<any>)=>{
      state.editPrepayCardId = action.payload;
    },
    paymentMethodsSliceSetInitialState: () => initialState,
  },

  extraReducers: (builder) => {
    builder.addCase(getlistOfPaymentMethods.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getlistOfPaymentMethods.fulfilled, (state, action) => {

      const defaultId = action.payload?.defaultPaymentMethodId;

      const defaultPaymentObj = 
      action.payload?.creditcard?.find((item: any) => item?.id === defaultId) || 
      action.payload?.paypal?.find((item: any) => item?.id === defaultId);

      if (defaultPaymentObj) {
        const type = defaultPaymentObj?.type?.toUpperCase();

        if (type?.includes("PAYPAL")){
          state.primaryPaymentMethod = "PAYPAL";
        } else if (type === "ATM" || defaultPaymentObj?.paymentType?.toUpperCase() === "ACH") {
          state.primaryPaymentMethod = "ACH";
        } else if (type === "CREDITCARD") {
          state.primaryPaymentMethod = "CREDIT_CARD";
        } else {
          state.primaryPaymentMethod = type;
        }
      } else {
        state.primaryPaymentMethod = null;
      }

      const nextExpCardDetails = checkAndGetCreditCardExiration(action.payload);
      state.nextCreditCardExpirationDate =
        nextExpCardDetails?.creditCardExpirationDate || "";
      state.nextCreditCardExpirationAllDetail =
        nextExpCardDetails?.nextMonthCreditCardExpDetails || null;
      state.paymentMethods = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getlistOfPaymentMethods.rejected, (state, action) => {
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "payment methods",
        action.error?.message,
        action?.type
      );
      state.paymentMethods = null;
      state.loading = false;
      state.error = action.error;
    });
  
    builder.addCase(getNonAuthPaymentDetails.pending, (state) => {
      state.nonAuthPaymentDetailsLoading = true;
    });
    builder.addCase(getNonAuthPaymentDetails.fulfilled, (state, action) => {
      const nextExpCardDetails = checkAndGetCreditCardExiration(action.payload);
      state.nextCreditCardExpirationDate =
        nextExpCardDetails?.creditCardExpirationDate || "";
      state.nextCreditCardExpirationAllDetail =
        nextExpCardDetails?.nextMonthCreditCardExpDetails || null;
      // Merge new response data with existing state data
      const existingData = state.nonAuthPaymentDetails?.data || {};
      const newData = action.payload?.data || {};
      state.nonAuthPaymentDetails = {
        ...(state.nonAuthPaymentDetails || {}),
        ...(action.payload || {}),
        data: {
          ...existingData,
          ...newData,
        },
      };
      state.nonAuthPaymentDetailsLoading = false;
      state.error = null;
    });
    builder.addCase(getNonAuthPaymentDetails.rejected, (state, action) => {
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "payment methods",
        action.error?.message,
        action?.type
      );
      state.nonAuthPaymentDetails = null;
      state.nonAuthPaymentDetailsLoading = false;
      state.error = action.error;
    });
  },
  

});

export const {
  hideCreditCardExpNotification,
  changePrimaryPaymentMethod,
  onAddEditPaymentMethodLoadingStart,
  onAddEditPaymentMethodLoadingStop,
  clearPaymentMethodsSlice,
  setIsListOfPaymentMethodDWLogCalled,
  setPaymentMethodAddSection,
  addAutoPaymentMethod,
  deleteAutoPaymentMethod,
  setOpenAutoPayAlertModal,
  setDefaultAutoPaymentMethodId,
  deleteAllAutoPaymentMethods,
  selectAutoPaymentMethodForEdit,
  setOpenConfirmCVVModal,
  setNextPrimaryPaymentMethod,
  seleteDeletePrimaryPaymentMethodFlag,
  deleteBankPaymentMethod,
  setSelectDeletePrimaryPaymentMethod,
  setAutoPaySettingsUpdated,
  editAutoPaymentMethod,
  paymentMethodsSliceSetInitialState,
  setShowEditPaymentPopUp,
  setPrepayCardId,
  setPrimaryPaymentMethodChanged,
  setaddPaymentMethodError
} = paymentMethodsSlice.actions;
export default paymentMethodsSlice.reducer;


